import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHmJobs from '../components/AdminHmJobs';
import AppShell, { storedSession } from '../components/AppShell';
import JobInbox from '../components/JobInbox';
import JobActions from '../components/jobs/JobActions';
import { JobDialog } from '../components/jobs/JobDialogs';
import LoadError, { isServerError } from '../components/jobs/LoadError';
import { AgeingPill, FunnelBar, FunnelHead, StageCells, StageHeads, STAGES, StatusPill } from '../components/jobs/Funnel';
import { api, btnPrimary, btnSecondary, card, EmptyState, InlineError, input, Pagination, Skeleton, ToastProvider, useToast } from '../components/ui/Kit';
import { formatDate } from '../utils/dates';
import { hasPermission } from '../utils/permissions';
import { intakeApi } from '../utils/jobIntake';

// /jobs — HM "My jobs", Recruiter "My jobs", Team jobs, All jobs (approved mockups HmJobs / RecJobs / TeamJobs /
// AdminJobs). The view, segment, filters and page live in the URL; the last view is remembered.
export const VIEW_LABEL = { hm: 'My jobs', recruiter: 'My jobs', team: 'Team jobs', admin: 'All jobs' };
const VIEW_SWITCH = { hm: 'My jobs · hiring manager', recruiter: 'My jobs · recruiter', team: 'Team jobs', admin: 'All jobs' };
const INTRO = {
  hm: 'Jobs you created. Hover a stage for Pending / Shortlisted / Rejected — click it to see those candidates.',
  recruiter: 'Jobs assigned to you. Open a job for its JD, screening questions and recruiter brief.',
  team: 'Every job your team is working on, grouped by who owns it. Assign new jobs to anyone who reports to you — or hand them to a lead.',
  admin: 'Every job in your company. “All stages” shows Pending / Shortlisted / Rejected per stage; scroll sideways for later stages.',
};
const SEG_LABEL = { all: 'All', draft: 'Drafts', in_approval: 'In approval', open: 'Open', on_hold: 'On hold', closed: 'Closed',
  primary: 'Primary only', to_assign: 'To assign', team: 'Team jobs' };
const FILTER_KEYS = ['view', 'segment', 'q', 'department', 'recruiter_id', 'hm_id', 'source', 'interviews', 'created_from', 'created_to', 'page', 'mode', 'sort', 'dir'];
// Sortable columns per list (the server sorts every page; default: recruiter = most recently assigned, others = opened)
const SORT_COLS = { hm: { Job: 'job', 'Recruiter(s)': 'recruiter', 'Opened · Ageing': 'opened' },
  recruiter: { Job: 'job', 'Hiring manager': 'hm', 'Days open / target': 'days' },
  team: { Job: 'job', 'Hiring manager': 'hm', 'Recruiter(s)': 'recruiter', 'Days open': 'days' },
  admin: { Job: 'job', Recruiter: 'recruiter', 'Hiring manager': 'hm', 'Days open': 'days' } };

function waitingText(w) {
  const d = Math.round(w.days || 0);
  return `Waiting on ${w.label}${w.who ? ` (${w.who})` : ''} · ${d < 1 ? 'today' : `${d} day${d === 1 ? '' : 's'}`}`;
}

function sentTo(row) {
  if ((row.recruiters || []).some((r) => !r.implicit)) {
    return <span data-testid="sent-to">{row.assigned_by ? <>Assigned by {row.assigned_by}</> : 'Assigned automatically'}</span>;
  }
  if (row.routed_to) return <>{row.routed_to.full_name}<div className="text-[11px] text-[#64748B]">assigns recruiters</div></>;
  return row.status === 'in_approval' ? <span className="text-[#94A3B8]">After approval</span> : '—';
}
const PRESETS = [{ name: 'All open jobs', filters: { segment: 'open' } }, { name: 'On hold', filters: { segment: 'on_hold' } },
  { name: 'Unassigned', filters: { segment: 'open', recruiter_id: 'unassigned' } }];
const LAST_VIEW = 'quali5d.jobs.view';
const LAST_VIEWS = 'quali5d.jobs.views';      // the views the server last offered this person

function readUrl() {
  const p = new URLSearchParams(window.location.search);
  return Object.fromEntries(FILTER_KEYS.filter((k) => p.get(k)).map((k) => [k, p.get(k)]));
}
function writeUrl(state) {
  const p = new URLSearchParams();
  FILTER_KEYS.forEach((k) => { if (state[k] && !(k === 'page' && state[k] === '1')) p.set(k, state[k]); });
  window.history.replaceState(null, '', `/jobs${p.toString() ? `?${p}` : ''}`);
}
const remember = (v, views) => {
  try { localStorage.setItem(LAST_VIEW, v); localStorage.setItem(LAST_VIEWS, JSON.stringify(views || [])); } catch (e) { /* private mode */ }
};
const lastView = () => { try { return localStorage.getItem(LAST_VIEW); } catch (e) { return null; } };
const lastViews = () => { try { return JSON.parse(localStorage.getItem(LAST_VIEWS) || '[]'); } catch (e) { return []; } };

// The server's rule (job_lists.views_for): admin → company admins; team → Receive & assign; hm → Create jobs;
// recruiter → Recruit. An admin always has admin + team; hm / recruiter depend on their jobs, so for an admin those
// are sent only if the server offered them last time. A view the person can't have is never sent (no 403 + retry).
export function viewAllowed(user, view) {
  if (!view || !user) return false;
  const admin = user.role === 'company_super_admin';
  if (view === 'admin') return admin;
  if (view === 'team') return admin || hasPermission(user, 'receive_assign');
  if (view === 'hm') return admin ? lastViews().includes('hm') : hasPermission(user, 'create_jobs');
  if (view === 'recruiter') return admin ? lastViews().includes('recruiter') : hasPermission(user, 'recruit');
  return false;
}

function People({ row }) {
  const recs = row.recruiters || [];
  if (!recs.length) return <span className="text-[#94A3B8]">{row.status === 'open' || row.status === 'on_hold' ? 'Not assigned yet' : '—'}</span>;
  const p = recs.find((r) => r.is_primary) || recs[0];
  return (
    <span>{p.full_name} <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#E8F1FB] text-[#003D82]">Primary</span>
      {recs.length > 1 && <span className="block text-xs text-[#64748B]">+ {recs.filter((r) => r !== p).map((r) => r.full_name).join(', ')}</span>}</span>
  );
}

function JobCell({ row, showLocation }) {
  const sub = [row.req_id, showLocation ? row.location : row.department, showLocation ? row.work_mode : null,
    row.openings ? `${row.openings} opening${row.openings > 1 ? 's' : ''}` : null].filter(Boolean).join(' · ');
  return (
    <div className="min-w-0"><div className="font-bold text-[#0F172A]">{row.job_title}
      {row.data_issue && <span className="ml-2 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FEF3C7] text-[#92400E]" title="Some of this job's details couldn't be read — counts show 0. Open it, or tell your admin." data-testid="data-issue">Data issue</span>}</div>
      <div className="text-xs text-[#64748B]">{sub}</div></div>
  );
}

function hotStage(view, row) {
  return view === 'hm' && row.needs_you ? (row.needs_you_column || 'ai_screen') : null;
}

const STAGE_NAME = Object.fromEntries(STAGES.map((x) => [x.key, x.label]));

function Kpis({ k }) {
  const items = [['Assigned to me', k.assigned, `${k.primary} as primary`], ['New candidates to review', k.new_to_review, `across ${k.new_jobs} job${k.new_jobs === 1 ? '' : 's'}`],
    ['Stuck past target', k.stuck, k.stuck ? `oldest ${k.oldest_stuck_days} days in ${STAGE_NAME[k.oldest_stuck_stage] || 'a stage'}` : 'nothing stuck'],
    ['Interviews this week', k.interviews_this_week, `${k.interviews_today} today`]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpis">
      {items.map(([label, v, sub]) => (
        <div key={label} className={card + ' p-4'}><p className="text-xs font-semibold text-[#64748B]">{label}</p>
          <p className="text-3xl font-extrabold mt-1">{v}</p><p className="text-xs text-[#64748B] mt-1">{sub}</p></div>
      ))}
    </div>
  );
}

function Workload({ people }) {
  return (
    <div className="flex flex-wrap gap-3" data-testid="workload">
      {people.map((p) => {
        const pct = Math.min(100, Math.round((p.open_jobs / Math.max(1, p.capacity)) * 100));
        return (
          <div key={p.user_id} className={card + ' p-4 min-w-[200px] flex-1'}>
            <p className="text-sm font-bold">{p.full_name}{p.you ? ' (you)' : ''}</p>
            <p className="text-xs text-[#64748B]">{[p.designation, p.is_lead ? `team of ${p.team_size}` : null, !p.is_lead && p.reports_to ? `reports to ${p.reports_to}` : null].filter(Boolean).join(' · ')}</p>
            {p.you ? <p className="text-sm mt-2">Recruiting myself <b>{p.open_jobs}</b> job{p.open_jobs === 1 ? '' : 's'}</p> : (
              <>
                <p className="text-xs text-[#64748B] mt-2">Open jobs / capacity <b className="text-[#0F172A]">{p.open_jobs} / {p.capacity}</b></p>
                <div className="h-1.5 rounded-full bg-[#EEF1F6] mt-1"><div className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-[#DC2626]' : pct >= 80 ? 'bg-[#F59E0B]' : 'bg-[#0066CC]'}`} style={{ width: `${pct}%` }} /></div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Team view: group by owner (Unassigned · you · each Primary recruiter · leads holding jobs to assign)
export function groupRows(rows, me) {
  const groups = [];
  const by = {};
  const add = (key, head, row) => { if (!by[key]) { by[key] = { key, head, rows: [] }; groups.push(by[key]); } by[key].rows.push(row); };
  rows.forEach((r) => {
    const primary = (r.recruiters || []).find((x) => x.is_primary);
    if (!primary) {
      if (r.routed_to && r.routed_to.user_id !== me) add(`lead.${r.routed_to.user_id}`, { title: r.routed_to.full_name, sub: 'waiting for them to assign' }, r);
      else add('unassigned', { title: 'Unassigned', sub: 'Approved and routed to you' }, r);
    } else if (primary.user_id === me) add('me', { title: 'You', sub: 'Recruiting yourself' }, r);
    else add(`u.${primary.user_id}`, { title: primary.full_name, sub: null }, r);
  });
  const order = (g) => (g.key === 'unassigned' ? 0 : g.key === 'me' ? 1 : g.key.startsWith('lead') ? 3 : 2);
  return groups.sort((a, b) => order(a) - order(b));
}

function Row({ view, row, mode, maxRounds, onDialog }) {
  const funnelOrGrid = mode === 'stages'
    ? <StageCells row={row} maxRounds={maxRounds} onOpen={(column, o) => onDialog('stage', row, { column, ...o })} />
    : <td className="px-3 py-3"><FunnelBar stages={row.stages} hotStage={hotStage(view, row)} onOpen={(column, o) => onDialog('stage', row, { column, ...o })} /></td>;
  const actions = <td className="px-3 py-3"><JobActions view={view} row={row} onDialog={onDialog} /></td>;
  const days = <td className="px-3 py-3 whitespace-nowrap">{row.days_open}<span className="text-[#94A3B8]"> / {row.target_days}</span></td>;
  if (view === 'hm') {
    return (
      <tr className="border-b border-[#EEF1F6]">
        <td className="px-3 py-3 sticky left-0 bg-white"><JobCell row={row} /></td>
        <td className="px-3 py-3"><StatusPill status={row.status} sentBack={row.sent_back} />
          {(row.waiting || []).map((w) => <div key={w.label + w.who} className="mt-1"><span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FEF3C7] text-[#92400E]" data-testid="waiting-chip">{waitingText(w)}</span></div>)}
          {row.status === 'draft' && row.intake_id && <div className="text-xs text-[#64748B] mt-0.5">Continue with Jessica</div>}</td>
        <td className="px-3 py-3">{sentTo(row)}</td>
        <td className="px-3 py-3"><People row={row} /></td>
        <td className="px-3 py-3 whitespace-nowrap">{formatDate(row.opened_at)}<div><AgeingPill days={row.days_open} tone={row.ageing} /></div></td>
        {funnelOrGrid}
        <td className="px-3 py-3">{row.needs_you ? <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FEF3C7] text-[#92400E]">{row.needs_you} to decide</span> : <span className="text-[#94A3B8]">—</span>}
          {row.decision_overdue > 0 && <span className="block mt-1 px-2 py-0.5 w-max rounded-full text-[11px] font-semibold bg-[#FEE2E2] text-[#991B1B]" data-testid="decision-overdue">{row.decision_overdue} decision overdue</span>}</td>
        {actions}
      </tr>
    );
  }
  if (view === 'recruiter') {
    const others = (row.recruiters || []).filter((r) => !r.is_primary);
    const primary = (row.recruiters || []).find((r) => r.is_primary);
    return (
      <tr className="border-b border-[#EEF1F6]">
        <td className="px-3 py-3 sticky left-0 bg-white"><JobCell row={row} showLocation /></td>
        <td className="px-3 py-3">{row.hiring_manager.full_name || '—'}</td>
        <td className="px-3 py-3">{row.my_role === 'primary'
          ? <><span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#E8F1FB] text-[#003D82]">Primary</span>{others.length > 0 && <div className="text-xs text-[#64748B]">with {others.map((o) => o.full_name).join(', ')}</div>}</>
          : <><span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#F1F5F9] text-[#475569]">Secondary</span>{primary && <div className="text-xs text-[#64748B]">primary: {primary.full_name}</div>}</>}</td>
        <td className={`px-3 py-3 whitespace-nowrap font-semibold ${row.days_open > row.target_days ? 'text-[#991B1B]' : 'text-[#0F172A]'}`}>{row.days_open} / {row.target_days}</td>
        {funnelOrGrid}
        <td className="px-3 py-3">{row.new || <span className="text-[#94A3B8]">—</span>}</td>
        <td className="px-3 py-3">{row.stuck ? <span className="font-bold text-[#B91C1C]">{row.stuck}</span> : <span className="text-[#94A3B8]">—</span>}</td>
        {actions}
      </tr>
    );
  }
  if (view === 'team') {
    return (
      <tr className="border-b border-[#EEF1F6]">
        <td className="px-3 py-3 sticky left-0 bg-white"><JobCell row={row} /></td>
        <td className="px-3 py-3">{row.hiring_manager.full_name || '—'}</td>
        <td className="px-3 py-3"><People row={row} /></td>
        <td className="px-3 py-3 text-[#334155]">{row.recruiters && row.recruiters.length ? (row.assigned_by || '—') : `Approved ${formatDate(row.approved_at)}`}</td>
        <td className="px-3 py-3 whitespace-nowrap"><AgeingPill days={row.days_open} tone={row.ageing} /></td>
        {funnelOrGrid}
        {actions}
      </tr>
    );
  }
  return (
    <tr className="border-b border-[#EEF1F6]">
      <td className="px-3 py-3 sticky left-0 bg-white z-10 min-w-[220px]"><JobCell row={row} /><div className="mt-1"><StatusPill status={row.status} sentBack={row.sent_back} /></div></td>
      <td className="px-3 py-3"><People row={row} /></td>
      <td className="px-3 py-3">{row.hiring_manager.full_name || '—'}</td>
      {days}
      {funnelOrGrid}
      {actions}
    </tr>
  );
}

function Heads({ view, mode, maxRounds, sort, dir, onSort }) {
  const fun = mode === 'stages' ? null : <th className="px-3 py-3"><FunnelHead /></th>;
  const label = (x) => {
    const key = (SORT_COLS[view] || {})[x];
    if (!key || !onSort) return x;
    const on = sort === key;
    return (
      <button type="button" className={`inline-flex items-center gap-1 uppercase tracking-[.05em] font-bold ${on ? 'text-[#0F172A]' : ''}`}
        onClick={() => onSort(key)} aria-label={`Sort by ${x}${on ? (dir === 'asc' ? ', ascending' : ', descending') : ''}`}>
        {x}<span aria-hidden="true" className={on ? '' : 'opacity-30'}>{on && dir === 'asc' ? '▲' : '▼'}</span></button>
    );
  };
  const aria = (x) => ((SORT_COLS[view] || {})[x] && sort === SORT_COLS[view][x] ? (dir === 'asc' ? 'ascending' : 'descending') : undefined);
  const th = (x) => <th key={x} scope="col" aria-sort={aria(x)} className="px-3 py-3 text-left whitespace-nowrap">{label(x)}</th>;
  const cols = { hm: ['Job', 'Status', 'Sent to', 'Recruiter(s)', 'Opened · Ageing'], recruiter: ['Job', 'Hiring manager', 'My role', 'Days open / target'],
    team: ['Job', 'Hiring manager', 'Recruiter(s)', 'Assigned by', 'Days open'], admin: ['Job', 'Recruiter', 'Hiring manager', 'Days open'] }[view];
  const after = { hm: ['Needs you', 'Actions'], recruiter: ['New', 'Stuck', 'Actions'], team: ['Actions'], admin: ['Actions'] }[view];
  const cls = 'text-[11px] text-[#64748B] font-bold uppercase tracking-[.05em] bg-[#FAFBFD] border-b border-[#E6E9F0]';
  if (mode === 'stages') {
    const h = StageHeads({ maxRounds });
    return (
      <thead className={cls}>
        <tr>{cols.map((c, i) => <th key={c} rowSpan={2} scope="col" aria-sort={aria(c)} className={`px-3 py-3 text-left whitespace-nowrap ${i === 0 ? 'sticky left-0 bg-[#FAFBFD] z-10' : ''}`}>{label(c)}</th>)}{h.top}{after.map((c) => <th key={c} rowSpan={2} className="px-3 py-3 text-left">{c}</th>)}</tr>
        <tr>{h.sub}</tr>
      </thead>
    );
  }
  return <thead className={cls}><tr>{cols.map(th)}{fun}{after.map(th)}</tr></thead>;
}

function MobileCard({ view, row, onDialog }) {
  return (
    <li className={card + ' p-4'} data-testid="job-card">
      <div className="flex items-start justify-between gap-2"><JobCell row={row} /><StatusPill status={row.status} sentBack={row.sent_back} /></div>
      <div className="text-xs text-[#64748B] mt-2">HM {row.hiring_manager.full_name || '—'} · {row.recruiters && row.recruiters.length ? (row.recruiters.find((r) => r.is_primary) || row.recruiters[0]).full_name : 'not assigned'} · <AgeingPill days={row.days_open} tone={row.ageing} /></div>
      <div className="overflow-x-auto mt-3 pb-1"><FunnelBar stages={row.stages} hotStage={hotStage(view, row)} onOpen={(column, o) => onDialog('stage', row, { column, ...o })} /></div>
      <div className="mt-3"><JobActions view={view} row={row} onDialog={onDialog} /></div>
    </li>
  );
}

function AdminTools({ data, f, set }) {
  const toast = useToast();
  const [views, setViews] = useState([]);
  const [name, setName] = useState('');
  useEffect(() => { api('/api/jobs/saved-views').then((r) => setViews(r.views || [])).catch(() => {}); }, []);
  const opts = data.filters || { departments: [], recruiters: [], hiring_managers: [] };
  const apply = (filters) => set({ ...Object.fromEntries(FILTER_KEYS.filter((k) => !['view', 'mode'].includes(k)).map((k) => [k, ''])), ...filters, page: '1' });
  const save = async () => {
    if (!name.trim()) return;
    const filters = Object.fromEntries(['segment', 'q', 'department', 'recruiter_id', 'hm_id', 'source', 'interviews', 'created_from', 'created_to', 'sort', 'dir'].filter((k) => f[k]).map((k) => [k, f[k]]));
    try { setViews((await api('/api/jobs/saved-views', { method: 'POST', body: { name: name.trim(), filters } })).views); setName(''); toast.show('View saved'); }
    catch (e) { toast.show(e.message, 'error'); }
  };
  const remove = async (id) => { try { setViews((await api(`/api/jobs/saved-views/${id}`, { method: 'DELETE' })).views); } catch (e) { toast.show(e.message, 'error'); } };
  const exportCsv = async () => {
    const p = new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([k, v]) => v && !['view', 'mode', 'page'].includes(k))));
    try {
      const res = await api(`/api/jobs/export.csv?${p}`, { raw: true });
      if (!res.ok) throw new Error('Export failed');
      const blob = new Blob([await res.text()], { type: 'text/csv' });
      const el = document.createElement('a'); el.href = URL.createObjectURL(blob); el.download = 'quali5d_jobs.csv'; el.click();
    } catch (e) { toast.show(e.message, 'error'); }
  };
  const sel = (key, label, items) => (
    <label className="flex flex-col gap-1 min-w-[150px]"><span className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest">{label}</span>
      <select aria-label={label} className={input + ' !py-1.5'} value={f[key] || ''} onChange={(e) => set({ [key]: e.target.value, page: '1' })}>{items}</select></label>
  );
  return (
    <div className={card + ' p-4 space-y-3'} data-testid="admin-tools">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold text-[#64748B] uppercase tracking-widest">Saved view</span>
        {PRESETS.map((v) => <button key={v.name} className={btnSecondary + ' !py-1 !min-h-0'} onClick={() => apply(v.filters)}>{v.name}</button>)}
        {views.map((v) => (
          <span key={v.id} className="inline-flex items-center gap-1"><button className={btnSecondary + ' !py-1'} onClick={() => apply(v.filters)}>{v.name}</button>
            <button aria-label={`Remove saved view ${v.name}`} className="text-[#94A3B8] hover:text-red-600" onClick={() => remove(v.id)}>×</button></span>
        ))}
        <input aria-label="Name this view" className={input + ' !w-40 !py-1'} placeholder="Name this view" value={name} onChange={(e) => setName(e.target.value)} />
        <button className={btnSecondary + ' !py-1'} disabled={!name.trim()} onClick={save}>Save view</button>
        <button className={btnPrimary + ' ml-auto'} onClick={exportCsv}>Export</button>
      </div>
      <div className="flex flex-wrap gap-3">
        {sel('department', 'Department', [<option key="" value="">All departments</option>, ...opts.departments.map((d) => <option key={d} value={d}>{d}</option>)])}
        {sel('recruiter_id', 'Recruiter', [<option key="" value="">All recruiters</option>, <option key="u" value="unassigned">Unassigned</option>, ...opts.recruiters.map((r) => <option key={r.id} value={r.id}>{r.full_name}</option>)])}
        {sel('hm_id', 'Hiring manager', [<option key="" value="">All hiring managers</option>, ...opts.hiring_managers.map((h) => <option key={h.id} value={h.id}>{h.full_name}</option>)])}
        {sel('source', 'Source', [<option key="" value="">All sources</option>, <option key="j" value="hm_jessica">Jessica (HM)</option>, <option key="a" value="ai_meeting">AI intake</option>, <option key="p" value="jd_upload">Phase 1 upload</option>])}
        {sel('interviews', 'Interviews', [<option key="" value="">All jobs</option>, ...[['has', 'Has interviews'], ['none', 'No interviews'], ['selected', 'Has selected'], ['rejected', 'Has rejected'], ['on_hold', 'Has on hold'], ['this_week', 'Interviews this week']].map(([v, l]) => <option key={v} value={v}>{l}</option>)])}
        <label className="flex flex-col gap-1"><span className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest">Created from</span>
          <input type="date" aria-label="Created from" className={input + ' !py-1.5'} value={f.created_from || ''} onChange={(e) => set({ created_from: e.target.value, page: '1' })} /></label>
        <label className="flex flex-col gap-1"><span className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest">Created to</span>
          <input type="date" aria-label="Created to" className={input + ' !py-1.5'} value={f.created_to || ''} onChange={(e) => set({ created_to: e.target.value, page: '1' })} /></label>
      </div>
    </div>
  );
}

function Inner() {
  const navigate = useNavigate();
  const { user } = storedSession();
  const [f, setF] = useState(() => readUrl());
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [adding, setAdding] = useState(false);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const load = useCallback(async () => {
    setError(''); setLoadError(null);
    const want = { ...f };
    if (!want.view && lastView()) want.view = lastView();
    if (want.view && !viewAllowed(user, want.view)) delete want.view;     // the server picks the person's default
    const p = new URLSearchParams(Object.fromEntries(Object.entries(want).filter(([k, v]) => v && k !== 'mode')));
    try {
      const d = await api(`/api/jobs?${p}`);
      setData(d);
      remember(d.view, d.views);
    } catch (e) {
      if (isServerError(e)) setLoadError(e); else setError(e.message);
      setData({ rows: [], views: [], counts: {}, total: 0, page: 1, page_size: 20 });
    }
  }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { writeUrl(f); load(); }, [f, load]);
  const view = data ? data.view : f.view;
  const mode = f.mode === 'stages' ? 'stages' : 'funnel';
  const groups = useMemo(() => (data && view === 'team' ? groupRows(data.rows, user.id) : null), [data, view, user.id]);
  const openDialog = (kind, job, extra = {}) => setDialog({ kind, job, ...extra });
  const addJob = async () => {
    setAdding(true);
    try { const job = await intakeApi('', { method: 'POST' }); navigate(`/jobs/intake/${job.id}`); } catch (e) { setError(e.message); setAdding(false); }
  };
  const maxRounds = data ? Math.max(1, data.max_rounds || 1) : 1;
  const table = (rows) => (
    <div className="overflow-x-auto hidden md:block">
      <table className={`w-full border-collapse text-[13px] ${mode === 'stages' ? 'min-w-[1400px]' : 'min-w-[1120px]'}`}>
        <Heads view={view} mode={mode} maxRounds={maxRounds} sort={data && data.sort} dir={data && data.dir}
          onSort={(key) => set({ sort: key, dir: data && data.sort === key ? (data.dir === 'asc' ? 'desc' : 'asc') : (['job', 'recruiter', 'hm'].includes(key) ? 'asc' : 'desc'), page: '1' })} />
        <tbody>{rows.map((r) => <Row key={r.id} view={view} row={r} mode={mode} maxRounds={maxRounds} onDialog={openDialog} />)}</tbody>
      </table>
    </div>
  );
  const cards = (rows) => <ul className="md:hidden space-y-3 p-3">{rows.map((r) => <MobileCard key={r.id} view={view} row={r} onDialog={openDialog} />)}</ul>;

  return (
    <AppShell active="jobs">
      <div className="space-y-4" data-testid="jobs-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div><h1 className="text-2xl font-extrabold">{VIEW_LABEL[view] || 'Jobs'}</h1>
            <p className="text-sm text-[#64748B] mt-1 max-w-3xl">{INTRO[view] || ''}</p></div>
          {view === 'hm' && <button className={btnPrimary} onClick={addJob} disabled={adding}>Add new job</button>}
        </div>
        {data && data.views.length > 1 && (
          <div role="tablist" aria-label="Jobs views" className="flex gap-1 p-1 bg-[#EEF1F6] rounded-xl w-max max-w-full overflow-x-auto">
            {data.views.map((v) => (
              <button key={v} role="tab" aria-selected={view === v} onClick={() => setF({ view: v })}
                className={`min-h-[36px] px-3.5 rounded-[9px] text-[13px] font-semibold whitespace-nowrap ${view === v ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#475569]'}`}>{VIEW_SWITCH[v]}</button>
            ))}
          </div>
        )}
        <JobInbox />
        {data && view === 'recruiter' && data.kpis && <Kpis k={data.kpis} />}
        {data && view === 'team' && data.workload && <Workload people={data.workload} />}
        {data && view === 'admin' && <AdminTools data={data} f={f} set={set} />}
        <InlineError>{error}</InlineError>
        <div className="flex flex-wrap items-center gap-3">
          <div role="tablist" aria-label="Job status" className="flex gap-1 p-1 bg-[#EEF1F6] rounded-xl max-w-full overflow-x-auto">
            {data && Object.keys(data.counts || {}).map((s) => (
              <button key={s} role="tab" aria-selected={data.segment === s} onClick={() => set({ segment: s, page: '1' })}
                className={`min-h-[36px] px-3.5 rounded-[9px] text-[13px] font-semibold whitespace-nowrap inline-flex items-center gap-1.5 ${data.segment === s ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#475569]'}`}>
                {SEG_LABEL[s] || s}{s !== 'closed' && s !== 'primary' && <span className="text-[#64748B]">{data.counts[s]}</span>}</button>
            ))}
          </div>
          <input aria-label="Search jobs" className={input + ' !w-56'} placeholder={view === 'team' ? 'Search team jobs' : 'Search jobs'} value={f.q || ''}
            onChange={(e) => set({ q: e.target.value, page: '1' })} />
          <div role="tablist" aria-label="Pipeline display" className="flex gap-1 p-1 bg-[#EEF1F6] rounded-xl ml-auto">
            {[['funnel', 'Funnel'], ['stages', 'All stages']].map(([k, l]) => (
              <button key={k} role="tab" aria-selected={mode === k} onClick={() => set({ mode: k === 'funnel' ? '' : 'stages' })}
                className={`min-h-[32px] px-3 rounded-[9px] text-[13px] font-semibold ${mode === k ? 'bg-white shadow-sm' : 'text-[#475569]'}`}>{l}</button>
            ))}
          </div>
        </div>
        {loadError ? <LoadError error={loadError} what="your jobs" onRetry={() => { setData(null); load(); }} /> : (
        <section className={card + ' overflow-hidden'}>
          {data === null ? (
            <div className="p-4 space-y-3" aria-busy="true">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : !data.rows.length ? (
            <EmptyState title={f.q || data.total === 0 ? 'No jobs here' : 'No jobs'} hint={view === 'hm' ? 'Add a new job and Jessica will help you write it.' :
              view === 'recruiter' ? 'Jobs assigned to you show up here.' : view === 'team' ? 'Jobs your team works on, and new ones to assign, show up here.' : 'Try another status or clear the filters.'} />
          ) : groups ? (
            groups.map((g) => {
              const open = expanded[g.key] || expanded.all;
              const shown = open ? g.rows : g.rows.slice(0, 2);
              const newCount = g.rows.reduce((s, r) => s + (r.new || 0), 0);
              const stuck = g.rows.reduce((s, r) => s + (r.stuck || 0), 0);
              return (
                <div key={g.key} className="border-b border-[#E6E9F0]" data-testid={`group-${g.key}`}>
                  <div className="px-4 py-2.5 bg-[#FAFBFD] text-sm flex flex-wrap gap-2 items-center">
                    <b>{g.head.title}</b>
                    <span className="text-[#64748B]">{[g.head.sub, `${g.rows.length} job${g.rows.length === 1 ? '' : 's'}`, newCount ? `${newCount} new candidates` : null].filter(Boolean).join(' · ')}</span>
                    {stuck > 0 && <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FEE2E2] text-[#991B1B]">{stuck} stuck</span>}
                  </div>
                  {table(shown)}{cards(shown)}
                  {g.rows.length > 2 && !open && (
                    <button className="w-full text-left px-4 py-2 text-sm font-semibold text-[#0066CC] hover:bg-[#F5F7FB]" onClick={() => setExpanded({ ...expanded, [g.key]: true })}>
                      Show {g.rows.length - 2} more of {g.head.title}'s jobs</button>
                  )}
                </div>
              );
            })
          ) : (<>{table(data.rows)}{cards(data.rows)}</>)}
          {groups && groups.some((g) => g.rows.length > 2) && !expanded.all && (
            <button className="px-4 py-2 text-sm font-semibold text-[#0066CC]" onClick={() => setExpanded({ all: true })}>Expand all</button>
          )}
          {data && data.total > data.page_size && (
            <Pagination page={data.page} pageSize={data.page_size} total={data.total} count={data.rows.length} noun="jobs" onPage={(pg) => set({ page: String(pg) })} />
          )}
        </section>
        )}
        {view === 'admin' && <p className="text-xs text-[#64748B]">Interview columns grow to the most rounds any job in the view uses. Hatched cells: that job doesn’t use the stage. Click any number to open those candidates.</p>}
        <p className="text-xs text-[#64748B]">Ageing: green &lt; 30 · amber 30–59 · red 60+ days. Red dot: stuck past target.</p>
        {view === 'admin' && <AdminHmJobs />}
      </div>
      <JobDialog dialog={dialog} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />
    </AppShell>
  );
}

export default function JobsPage() {
  const navigate = useNavigate();
  useEffect(() => { if (!localStorage.getItem('token')) navigate('/'); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <ToastProvider><Inner /></ToastProvider>;
}
